<template>
  <div>
    <c-box
      :border-radius="{ md: 'base' }"
      background="white"
      :box-shadow="{ md: 'base' }"
      :px="{ base: 'md', sm: '2xl', lg: '5rem' }"
      :py="{ base: 'md', sm: '3xl', lg: '3rem' }"
      :mx="{ base: '-31px', md: '0' }"
      :mb="{ base: 'md', md: '3xl' }"
      data-cy="delivery-details-section"
    >
      <c-heading
        as="h3"
        :mb="{ base: 'md', sm: '4xl' }"
        font-size="1.6rem"
        font-weight="normal"
        display="flex"
        :flex-direction="{ base: 'column', sm: 'row' }"
      >
        {{ $t("ordering.deliveryDetails.title") }}
        <c-text as="span" font-size="sm" ml="auto">
          <c-text as="span" color="brand.greenDark">* </c-text>
          {{ $t("common.mandatory") }}
        </c-text>
      </c-heading>

      <c-box class="row" mb="2xl">
        <!-- Delivery method -->
        <div class="col-md-6">
          <div class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('deliveryTypeCode')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="deliveryMethod">
                  {{ $t("ordering.deliveryDetails.deliveryMethod") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-select
                    id="deliveryMethod"
                    size="lg"
                    v-model="cart.deliveryDetails.deliveryTypeCode"
                    @blur="onFieldBlur('cart.deliveryDetails.deliveryTypeCode')"
                    @change="onFieldBlur('cart.deliveryDetails.deliveryTypeCode')"
                  >
                    <option
                      v-for="(deliveryMethod, index) in deliveryMethodList"
                      :key="index"
                      :value="deliveryMethod.v"
                    >
                      {{ deliveryMethod.t ? $t(deliveryMethod.t) : deliveryMethod.text }}
                    </option>
                  </c-select>

                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.deliveryTypeCode')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="delivery-method-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("ordering.deliveryDetails.deliveryMethod"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>
        </div>

        <!-- Requested delivery date -->
        <div class="col-md-6">
          <div class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('requestedDeliveryDate')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="requestedDeliveryDate">
                  {{ $t("ordering.deliveryDetails.shippingDate") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-box display="flex" position="relative" align-items="baseline">
                    <datepicker
                      id="requestedDeliveryDate"
                      v-chakra="
                        (theme) => ({
                          width: '100%',
                          '& input.form-control': {
                            cursor: 'pointer',
                            height: '3rem',
                            fontSize: `${theme.fontSizes.md} !important`,
                            background: `${theme.colors.white} url(${prepareIconForBg(
                              theme.icon.chakra.calendar.viewBox,
                              theme.icon.chakra.calendar.path,
                              theme.colors.brand.purple,
                            )}) no-repeat right 1rem center`,
                            backgroundSize: '1.25rem',
                            borderRadius: theme.radii.input,
                          },
                        })
                      "
                      v-model="cart.deliveryDetails.requestedDeliveryDate"
                      :class="getFieldClasses('cart.deliveryDetails.requestedDeliveryDate')"
                      input-class="form-control datepicker"
                      :monday-first="true"
                      :disabled-dates="datePickerState.disabledDates"
                      :format="customFormatter"
                      :language="getLanguageObject(user.languageCode)"
                      :use-utc="true"
                      @selected="onFieldBlur('cart.deliveryDetails.requestedDeliveryDate')"
                      @input="onFieldBlur('cart.deliveryDetails.requestedDeliveryDate')"
                      @cleared="onFieldBlur('cart.deliveryDetails.requestedDeliveryDate')"
                    />
                  </c-box>
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.requestedDeliveryDate')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="requested-delivery-date-error"
                  >
                    <span>
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("ordering.deliveryDetails.shippingDate"),
                        })
                      }}
                    </span>
                  </c-box>
                  <c-box v-if="deliveryDelay > 1" font-size="sm" my="xs">
                    <span>
                      <c-text as="span" color="brand.greenDark">*</c-text>
                      {{
                        $t("ordering.deliveryDetails.deliveryDelay", {
                          days: deliveryDelay,
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>
        </div>
      </c-box>

      <div class="row">
        <div class="col-md-6">
          <!-- Country -->
          <div class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('shipToAddress.countryCode')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="shipToCountry">
                  {{ $t("common.address.country") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <span v-if="cart.deliveryDetails.shipToAddress">
                    <c-select
                      id="shipToCountry"
                      size="lg"
                      v-model="cart.deliveryDetails.shipToAddress.countryCode"
                      data-cy="countrySelector"
                      @blur="onFieldBlur('cart.deliveryDetails.shipToAddress.countryCode')"
                      @change="
                        (onFieldBlur('cart.deliveryDetails.shipToAddress.countryCode'),
                        updateDeliveryDelay())
                      "
                    >
                      <option
                        v-for="(country, index) in shipToCountryList"
                        :key="index"
                        :value="country.v"
                      >
                        {{ country.t }}
                      </option>
                    </c-select>
                  </span>
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.shipToAddress.countryCode')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="country-code-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("common.address.country"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>
          <!-- Street address -->
          <div class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('shipToAddress.streetAddress')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="shipToStreetAddress">
                  {{ $t("common.address.streetAddress") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="shipToStreetAddress"
                    size="lg"
                    v-model.trim="cart.deliveryDetails.shipToAddress.streetAddress"
                    @keyup="onFieldChange('cart.deliveryDetails.shipToAddress.streetAddress')"
                    @blur="onFieldBlur('cart.deliveryDetails.shipToAddress.streetAddress')"
                  />
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.shipToAddress.streetAddress')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="street-address-error"
                  >
                    <span v-if="cart.deliveryDetails.shipToAddress.streetAddress === ''">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("common.address.streetAddress"),
                        })
                      }}
                    </span>
                    <span v-else-if="cart.deliveryDetails.shipToAddress.streetAddress !== ''">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("common.address.streetAddress"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>

          <!-- Street address 2 -->
          <div class="form-group">
            <c-form-control :is-invalid="inputIsInvalid('shipToAddress.streetAddress2')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="shipToStreetAddress2">
                  {{ `${$t("common.address.streetAddress")} 2` }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="shipToStreetAddress2"
                    size="lg"
                    v-model.trim="cart.deliveryDetails.shipToAddress.streetAddress2"
                    @keyup="onFieldChange('cart.deliveryDetails.shipToAddress.streetAddress2')"
                    @blur="onFieldBlur('cart.deliveryDetails.shipToAddress.streetAddress2')"
                  />

                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.shipToAddress.streetAddress2')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="street-address-2-error"
                  >
                    <span v-if="cart.deliveryDetails.shipToAddress.streetAddress !== ''">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: `${$t("common.address.streetAddress")} 2`,
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>
        </div>

        <!-- Zip code -->
        <c-box class="col-md-6 order-5">
          <div class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('shipToAddress.zipCode')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="shipToZipCode">
                  {{ $t("common.address.zipCode") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    :disabled="
                      countriesWithoutPostalCodesList.includes(
                        cart.deliveryDetails.shipToAddress.countryCode,
                      )
                    "
                    id="shipToZipCode"
                    size="lg"
                    v-model.trim="cart.deliveryDetails.shipToAddress.zipCode"
                    @keyup="onFieldChange('cart.deliveryDetails.shipToAddress.zipCode')"
                    @blur="onFieldBlur('cart.deliveryDetails.shipToAddress.zipCode')"
                  />
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.shipToAddress.zipCode')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="zip-code-error"
                  >
                    <span v-if="cart.deliveryDetails.shipToAddress.zipCode === ''">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("common.address.zipCode"),
                        })
                      }}
                    </span>
                    <span v-else-if="cart.deliveryDetails.shipToAddress.zipCode !== ''">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("common.address.zipCode"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>

          <!-- City -->
          <c-form-control :is-invalid="inputIsInvalid('shipToAddress.city')" is-required>
            <c-flex
              d="flex"
              :ml="{ xl: 'md' }"
              mb="lg"
              min-height="46px"
              :align-items="cityNotSelectedFromListMessage ? 'baseline' : 'center'"
              overflow="visible"
            >
              <c-form-label
                :min-width="{ xl: '12rem' }"
                :mr="{ base: 'lg', xl: '0' }"
                font-weight="400"
              >
                {{ $t("common.address.city") }}
              </c-form-label>
              <Spinner v-if="cityLoading" size="md" />
              <c-box v-else :text-align="{ base: 'right', lg: 'left' }" flex="1" width="100%">
                <c-box
                  v-if="
                    countriesWithoutPostalCodesList.includes(
                      cart.deliveryDetails.shipToAddress.countryCode,
                    )
                  "
                >
                  <QuickCitySearch
                    width="100%"
                    :countryCode="cart.deliveryDetails.shipToAddress.countryCode"
                    :isPickup="false"
                    :isDHL="true"
                    :zipCode="cart.deliveryDetails.shipToAddress.zipCode"
                    :city="cart.deliveryDetails.shipToAddress.city"
                    @set-delivery-city="setDeliveryCity"
                  >
                  </QuickCitySearch>
                </c-box>
                <c-text
                  v-else-if="
                    isErrorField('cart.deliveryDetails.shipToAddress.city') &&
                    (cart.deliveryDetails.shipToAddress.cities?.length ?? 0) < 1
                  "
                  as="span"
                  color="brand.red"
                  data-cy="city-error"
                >
                  <c-text as="span" v-if="cityErrorMessage" data-cy="address-city-error">
                    {{ $t(cityErrorMessage) }}
                  </c-text>
                  <c-text
                    v-else-if="missingFieldsForGettingCity"
                    as="span"
                    font-style="italic"
                    color="gray.600"
                  >
                    {{ $t("common.address.city.emptyWithDeliveryMethod") }}
                  </c-text>
                  <c-text as="span" v-else color="gray.600" data-cy="address-city-error">
                    {{ $t("common.address.city.unknownError") }}
                  </c-text>
                </c-text>
                <c-select
                  v-else-if="(cart.deliveryDetails.shipToAddress.cities?.length ?? 0) === 1"
                  id="city"
                  size="lg"
                  v-model="cart.deliveryDetails.shipToAddress.city"
                  data-cy="delivery-details-city-found"
                >
                  <option :value="cart.deliveryDetails.shipToAddress.city" selected>
                    {{ cart.deliveryDetails.shipToAddress.city }}
                  </option>
                </c-select>

                <c-select
                  v-else-if="(cart.deliveryDetails.shipToAddress.cities?.length ?? 0) > 1"
                  id="city"
                  size="lg"
                  v-model="cart.deliveryDetails.shipToAddress.city"
                  @blur="onFieldBlur('cart.deliveryDetails.shipToAddress.city')"
                  @change="onFieldBlur('cart.deliveryDetails.shipToAddress.city')"
                  data-cy="delivery-details-multiple-cities-found"
                >
                  <option
                    v-for="(city, index) in cart.deliveryDetails.shipToAddress.cities"
                    :key="index"
                    :value="city"
                    :selected="city === cart.deliveryDetails.shipToAddress.city"
                  >
                    {{ city }}
                  </option>
                </c-select>
                <c-text v-else as="span" font-style="italic" color="gray.600">
                  {{ $t("common.address.city.emptyWithDeliveryMethod") }}
                </c-text>

                <c-text
                  v-if="
                    cityNotSelectedFromListMessage &&
                    !countriesWithoutPostalCodesList.includes(
                      cart.deliveryDetails.shipToAddress.countryCode,
                    )
                  "
                  color="brand.red"
                  data-cy="city-not-selected-error"
                  mt="xs"
                >
                  {{ $t(cityNotSelectedFromListMessage) }}
                </c-text>
              </c-box>
            </c-flex>
          </c-form-control>
          <!-- province code -->
          <c-box v-if="countrySubdivisionList.length">
            <c-form-control
              is-required
              :is-invalid="inputIsInvalid('shipToAddress.stateProvinceCode')"
            >
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="stateProvinceCode">
                  {{
                    $t(
                      `common.address.countrySubdivision.${cart.deliveryDetails.shipToAddress.countryCode}`,
                    )
                  }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-select
                    id="stateProvinceCode"
                    size="lg"
                    v-model="cart.deliveryDetails.shipToAddress.stateProvinceCode"
                    data-cy="province-code-selector"
                    @blur="onFieldBlur('cart.deliveryDetails.shipToAddress.stateProvinceCode')"
                    @change="onFieldBlur('cart.deliveryDetails.shipToAddress.stateProvinceCode')"
                  >
                    <option
                      v-for="(countrySubdivision, index) in countrySubdivisionList"
                      :key="index"
                      :value="countrySubdivision.v"
                    >
                      {{ countrySubdivision.t }}
                    </option>
                  </c-select>

                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.shipToAddress.stateProvinceCode')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="province-code-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t(
                          `common.address.countrySubdivision.${cart.deliveryDetails.shipToAddress.countryCode}`,
                        ),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>
        </c-box>
      </div>
    </c-box>

    <c-box
      :border-radius="{ md: 'base' }"
      background="white"
      :box-shadow="{ md: 'base' }"
      :px="{ base: 'md', sm: '2xl', lg: '5rem' }"
      :py="{ base: 'md', sm: '3xl', lg: '3rem' }"
      :mx="{ base: '-31px', md: '0' }"
      :mb="{ base: 'md', md: '3xl' }"
      data-cy="delivery-details-section"
    >
      <c-heading
        as="h3"
        :mb="{ base: 'md', sm: '4xl' }"
        font-size="1.6rem"
        font-weight="normal"
        display="flex"
        :flex-direction="{ base: 'column', sm: 'row' }"
      >
        {{ $t("ordering.recipientInformation.title") }}
        <c-text as="span" font-size="sm" ml="auto">
          <c-text as="span" color="brand.greenDark">* </c-text>
          {{ $t("common.mandatory") }}
        </c-text>
      </c-heading>

      <div class="row">
        <div class="col-md-6">
          <!-- Company name -->
          <div class="form-group">
            <c-form-control :is-invalid="inputIsInvalid('companyName')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="companyName">
                  {{ $t("common.companyName") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="companyName"
                    size="lg"
                    flex="1"
                    v-model.trim="cart.deliveryDetails.companyName"
                    @keyup="onFieldChange('cart.deliveryDetails.companyName')"
                    @blur="onFieldBlur('cart.deliveryDetails.companyName')"
                  />
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.companyName')"
                    color="brand.red"
                    data-cy="company-name-error"
                  >
                    <span v-if="cart.deliveryDetails.companyName === ''">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("common.companyName"),
                        })
                      }}
                    </span>
                    <span v-else-if="cart.deliveryDetails.companyName !== ''">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("common.user.companyName"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>

          <!-- Receiver name -->
          <div class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('receiverName')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="receiverName">
                  {{ $t("common.recipientName") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="receiverName"
                    size="lg"
                    v-model.trim="cart.deliveryDetails.receiverName"
                    @keyup="onFieldChange('cart.deliveryDetails.receiverName')"
                    @blur="onFieldBlur('cart.deliveryDetails.receiverName')"
                  />
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.receiverName')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="receiver-name-error"
                  >
                    <span v-if="cart.deliveryDetails.receiverName === ''">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("common.recipientName"),
                        })
                      }}
                    </span>
                    <span v-else-if="cart.deliveryDetails.receiverName !== ''">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("common.recipientName"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>

          <!-- Email -->
          <div class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('email')">
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :mr="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="receiverEmail">
                  {{ $t("common.user.email") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="receiverEmail"
                    size="lg"
                    v-model.trim="cart.deliveryDetails.email"
                    @keyup="onFieldChange('cart.deliveryDetails.email')"
                    @blur="onFieldBlur('cart.deliveryDetails.email')"
                  />
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.email')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="email-error"
                  >
                    <span v-if="cart.deliveryDetails.email === ''">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("common.user.email"),
                        })
                      }}
                    </span>
                    <span v-else-if="cart.deliveryDetails.email !== ''">
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("common.user.email"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>
        </div>

        <!-- Phone number -->
        <div class="col-md-6 order-9 order-md-4">
          <div class="form-group">
            <c-form-control is-required>
              <c-box
                display="flex"
                :flex-direction="{ base: 'column', sm: 'row' }"
                :align-items="{ base: 'baseline', lg: 'inherit' }"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="phoneNumber" :mt="{ lg: 'sm' }">
                  {{ $t("common.user.phoneNumber") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <!-- NOTE: Since this phone number input does not support chakra-ui
                  without some hacky way, so it's better to hardcode the parameterized styles here. -->
                  <VuePhoneNumberInput
                    id="phoneNumber"
                    :border-radius="8"
                    color="#1A1B31"
                    valid-color="#131313"
                    error-color="#E5524C"
                    v-chakra="
                      (theme) => ({
                        '& div.select-country-container': {
                          height: '3rem',
                        },
                        '& input.country-selector__input': {
                          height: '3rem',
                        },
                        '& div.input-tel': {
                          height: '3rem',
                        },
                        '& input.input-tel__input': {
                          height: '3rem',
                        },
                      })
                    "
                    :no-country-selector="false"
                    :show-code-on-list="true"
                    :value="phoneNumberWithoutCountryCallingCode"
                    :default-country-code="defaultPhoneNumberCountryCode"
                    :preferred-countries="['FI', 'SE', 'DE', 'NO', 'DK', 'NL', 'FR', 'ES']"
                    :translations="{
                      countrySelectorLabel: $t('common.user.phoneNumberCountryCode'),
                      countrySelectorError: '',
                      phoneNumberLabel: '',
                      example: '',
                    }"
                    @update="setPhoneNumber"
                    @input="setPhoneNumberInput"
                  />
                  <c-box
                    v-if="isErrorField('phoneNumberInputValue')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="phone-number-error"
                  >
                    <span v-if="!getValidationField('phoneNumberInputValue').required">
                      {{
                        $t("common.error.fieldRequired", {
                          field: $t("common.user.phoneNumber"),
                        })
                      }}
                    </span>

                    <span v-if="!getValidationField('phoneNumberInputValue').maxLength">
                      {{
                        $t("common.error.fieldTooLong", {
                          field: $t("common.user.phoneNumber"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>

          <!-- Customer reference -->
          <div class="form-group">
            <c-form-control :is-invalid="inputIsInvalid('reference')">
              <c-box
                display="flex"
                position="relative"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="customerReference">
                  {{ $t("ordering.deliveryDetails.customerReference") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="customerReference"
                    size="lg"
                    v-model.trim="cart.deliveryDetails.reference"
                    @keyup="onFieldChange('cart.deliveryDetails.reference')"
                    @blur="onFieldBlur('cart.deliveryDetails.reference')"
                  />

                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.reference')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="customer-reference-error"
                  >
                    <span>
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("ordering.deliveryDetails.customerReference"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>

          <!-- External order number -->
          <div class="form-group">
            <c-form-control :is-invalid="inputIsInvalid('externalOrderNumber')">
              <c-box
                display="flex"
                position="relative"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
                :ml="{ xl: 'md' }"
              >
                <c-form-label :min-width="{ xl: '12rem' }" for="externalOrderNumber">
                  {{ $t("ordering.deliveryDetails.externalOrderNumber") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="externalOrderNumber"
                    size="lg"
                    v-model.trim="cart.deliveryDetails.externalOrderNumber"
                    @keyup="onFieldChange('cart.deliveryDetails.externalOrderNumber')"
                    @blur="onFieldBlur('cart.deliveryDetails.externalOrderNumber')"
                  />
                  <c-box
                    v-if="isErrorField('cart.deliveryDetails.externalOrderNumber')"
                    color="brand.red"
                    font-size="sm"
                    my="xs"
                    data-cy="external-order-number-error"
                  >
                    <span>
                      {{
                        $t("common.error.fieldInvalid", {
                          field: $t("ordering.deliveryDetails.externalOrderNumber"),
                        })
                      }}
                    </span>
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </div>
        </div>

        <!-- Additional details -->
        <div class="col-md-12 order-12 order-md-12">
          <c-form-control
            :is-invalid="inputIsInvalid('additionalDetails')"
            :mb="{ base: '0', sm: 'lg' }"
          >
            <c-box
              display="flex"
              position="relative"
              :flex-direction="{ base: 'column', sm: 'row' }"
              align-items="flex-start"
            >
              <c-form-label :width="{ xl: '12rem' }" :mt="{ md: '0.5rem' }" for="additionalDetails">
                {{ $t("ordering.deliveryDetails.additionalDetails") }}
                {{ $t("ordering.deliveryDetails.additionalDetailsNote") }}
              </c-form-label>
              <c-box flex="1" width="100%">
                <c-textarea
                  id="additionalDetails"
                  v-chakra
                  width="100%"
                  p="md"
                  font-size="1rem !important"
                  border-radius="input"
                  v-model.trim="cart.deliveryDetails.additionalDetails"
                  @keyup="onFieldChange('cart.deliveryDetails.additionalDetails')"
                  @blur="onFieldBlur('cart.deliveryDetails.additionalDetails')"
                />
                <c-box
                  v-if="isErrorField('cart.deliveryDetails.additionalDetails')"
                  color="brand.red"
                  font-size="sm"
                  my="xs"
                  data-cy="additional-details-error"
                >
                  <span>
                    {{
                      $t("common.error.fieldInvalid", {
                        field: $t("ordering.deliveryDetails.additionalDetails"),
                      })
                    }}
                  </span>
                </c-box>
              </c-box>
            </c-box>
          </c-form-control>
        </div>
      </div>
    </c-box>
  </div>
</template>

<script lang="ts">
import {
  countriesWithoutPostalCodesList,
  countryList,
  countrySubdivisionList,
  ICountrySubdivision,
  IRootState,
  ICartState,
} from "@iloq/shared";
import { mapState } from "vuex";

import QuickCitySearch from "@/components/products/QuickCitySearch.vue";
import Spinner from "@/components/Spinner.vue";
import { IPartnerInfoState } from "@/store/partnerInfoModule";
import { datepickerMixin } from "@/util/DatepickerMixin";
import {
  CBox,
  CFlex,
  CFormControl,
  CFormLabel,
  CHeading,
  CIcon,
  CInput,
  CSelect,
  CText,
  CTextarea,
  CTooltip,
} from "@chakra-ui/vue";
import { debounce, DebouncedFunc } from "lodash";
import { defineComponent, PropType } from "vue";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import Datepicker from "vuejs-datepicker";
import { inputMixin } from "./OrderMixins";

export default defineComponent({
  name: "DeliveryDetails",
  mixins: [inputMixin, datepickerMixin],
  components: {
    Datepicker,
    VuePhoneNumberInput,
    CHeading,
    CFormLabel,
    CBox,
    CFormControl,
    CInput,
    CSelect,
    CIcon,
    CTextarea,
    CText,
    CTooltip,
    Spinner,
    CFlex,
    QuickCitySearch,
  },
  props: {
    cart: {
      type: Object as PropType<ICartState>,
      required: true,
    },
    $v: {
      type: Object,
      required: true,
    },
    onFieldBlur: {
      type: Function,
      required: true,
    },
    getFieldClasses: {
      type: Function,
      required: true,
    },
    isErrorField: {
      type: Function,
      required: true,
    },
    getValidationField: {
      type: Function,
      required: true,
    },
    onFieldChange: {
      type: Function,
      required: true,
    },
    countryAndZipCodeUpdated: {
      type: Function as PropType<() => Promise<void>>,
      required: true,
    },
    deliveryMethodList: {
      type: Array as PropType<any[]>,
      required: true,
    },
    projectInputForValidator: {
      type: String,
      required: true,
    },
    deliveryDelay: {
      type: Number,
      required: true,
    },
    errors: {
      type: Array as PropType<{ field: string; message: string | null }[]>,
      required: true,
    },
    updateDeliveryDelay: {
      type: Function,
      required: true,
    },
    datePickerState: {
      type: Object as PropType<{
        disabledDates: {
          to: Date;
          days: [number, number];
          dates: Date[];
        };
      }>,
      required: true,
    },
    phoneNumberWithoutCountryCallingCode: {
      type: String,
      required: true,
    },
    defaultPhoneNumberCountryCode: {
      type: String,
      required: true,
    },
    phoneNumberInputValue: {
      type: String,
      required: true,
    },
    setPhoneNumber: {
      type: Function,
      required: true,
    },
    setPhoneNumberInput: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      cityLoading: false,
      debouncedAddressChange: undefined as DebouncedFunc<any> | undefined,
      countrySubdivisionList: [] as ICountrySubdivision[],
      countriesWithoutPostalCodesList: countriesWithoutPostalCodesList(),
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    ...mapState("partnerInfo", {
      deliveryTypes: (state: unknown) => (state as IPartnerInfoState).deliveryTypes,
    }),
    cityErrorMessage(): string | undefined {
      return this.errors?.find(
        (err) => err.field === "cart.deliveryDetails.shipToAddress.city" && err.message !== null,
      )?.message as string | undefined;
    },
    cityNotSelectedFromListMessage(): string | undefined {
      return this.errors?.find(
        (err) =>
          err.field === "cart.deliveryDetails.shipToAddress.city" &&
          err.message === "common.address.city.notSelectedFromListOfCities",
      )?.message as string | undefined;
    },
    shipToCountryList(): any {
      return countryList();
    },
    missingFieldsForGettingCity(): boolean {
      return (
        !this.cart.deliveryDetails.shipToAddress.countryCode ||
        !this.cart.deliveryDetails.shipToAddress.zipCode ||
        !this.cart.deliveryDetails.deliveryTypeCode
      );
    },
  },
  methods: {
    inputIsInvalid(inputName: string, complete?: boolean): boolean {
      const name = complete ? inputName : `cart.deliveryDetails.${inputName}`;
      return this.getFieldClasses(name)?.["is-invalid"] === true;
    },
    handleStateProvinceCodeField(stateProvinceCode = "") {
      this.$set(this.cart.deliveryDetails.shipToAddress, "stateProvinceCode", undefined);
      if (this.cart.deliveryDetails.shipToAddress.countryCode) {
        this.countrySubdivisionList =
          countrySubdivisionList(this.cart.deliveryDetails.shipToAddress.countryCode) || [];

        if (this.countrySubdivisionList.length > 0) {
          this.$set(
            this.cart.deliveryDetails.shipToAddress,
            "stateProvinceCode",
            stateProvinceCode,
          );
        }
      } else {
        this.countrySubdivisionList = [];
      }
    },
    async handleCountryCodeAndZipCodeValidationAndCityUpdate() {
      this.cityLoading = true;
      try {
        await this.countryAndZipCodeUpdated();
      } catch {
        // Ignoring errors...
      }
      this.cityLoading = false;
    },
    async setDeliveryCity(city: string) {
      this.cart.deliveryDetails.shipToAddress.city = city;
    },
    watchDeliveryMethod() {
      this.debouncedAddressChange?.();
    },
    watchCountry() {
      this.handleStateProvinceCodeField("");
      this.debouncedAddressChange?.();
    },
    watchZipCode() {
      this.debouncedAddressChange?.();
    },
  },
  beforeMount() {
    if (this.cart.cartRows.length === 0) {
      this.$router.replace({
        query: {
          step: "1",
        },
      });
      return;
    }
    this.handleStateProvinceCodeField(this.cart.deliveryDetails.shipToAddress.stateProvinceCode);

    if (
      this.cart.deliveryDetails.shipToAddress.countryCode &&
      this.cart.deliveryDetails.shipToAddress.zipCode &&
      this.cart.deliveryDetails.deliveryTypeCode &&
      !this.cart.deliveryDetails.shipToAddress.cities?.length &&
      !this.countriesWithoutPostalCodesList.includes(
        this.cart.deliveryDetails.shipToAddress.countryCode,
      )
    ) {
      this.handleCountryCodeAndZipCodeValidationAndCityUpdate();
    }

    this.debouncedAddressChange = debounce(
      this.handleCountryCodeAndZipCodeValidationAndCityUpdate,
      500,
    );
  },
  watch: {
    "cart.deliveryDetails.deliveryTypeCode": "watchDeliveryMethod",
    "cart.deliveryDetails.shipToAddress.countryCode": "watchCountry",
    "cart.deliveryDetails.shipToAddress.zipCode": "watchZipCode",
  },
});
</script>
